import { useEffect, useState } from "react";
import PosAPI from "../api/inventory/posApi";
import { inr } from "../utils/posBilling";

const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const describe = (c) =>
  c.discountType === "PERCENTAGE"
    ? `${c.discountValue}% off${c.maximumDiscount ? ` (up to ${inr(c.maximumDiscount)})` : ""}`
    : `${inr(c.discountValue)} off`;

/**
 * applied     -> coupon object (Coupon model ke fields) ya null
 * billCoupon  -> computeBill() ka coupon result { eligible, shortBy, amount }
 */
export default function PosCouponBox({ applied, billCoupon, subtotal, customerId, onApply, onRemove }) {
  const [code, setCode] = useState("");
  const [available, setAvailable] = useState([]);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [showList, setShowList] = useState(false);

  useEffect(() => {
    PosAPI.coupons()
      .then((res) => res.code === 200 && setAvailable(res.data || []))
      .catch(() => {});
  }, []);

  const apply = async (value) => {
    const c = (value ?? code).trim().toUpperCase();
    if (!c) return;
    setChecking(true);
    setError("");
    try {
      const res = await PosAPI.checkCoupon({ code: c, subtotal, customerId: customerId || null });
      if (res.code === 200) {
        onApply(res.data);
        setCode("");
        setShowList(false);
      } else setError(res.message || "Invalid coupon");
    } catch (err) {
      setError(errMsg(err, "Invalid coupon"));
    } finally {
      setChecking(false);
    }
  };

  if (applied) {
    return (
      <div className={`pos-coupon-applied ${billCoupon && !billCoupon.eligible ? "is-warn" : ""}`}>
        <div className="d-flex justify-content-between align-items-start">
          <div className="overflow-hidden">
            <div className="fw-semibold">
              <i className="ti ti-ticket me-1" />
              {applied.code}
              {billCoupon?.eligible && <span className="text-success ms-2">− {inr(billCoupon.amount)}</span>}
            </div>
            <small className="text-muted d-block text-truncate">
              {applied.title} · {describe(applied)}
            </small>
            {billCoupon && !billCoupon.eligible && (
              <small className="text-danger d-block">
                Add {inr(billCoupon.shortBy)} more to use this coupon (minimum {inr(applied.minimumCartValue)}).
              </small>
            )}
          </div>
          <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={onRemove} title="Remove coupon">
            <i className="ti ti-x" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="input-group input-group-sm">
        <span className="input-group-text">
          <i className="ti ti-ticket" />
        </span>
        <input
          className="form-control text-uppercase"
          placeholder="Coupon code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            setError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              apply();
            }
          }}
        />
        <button type="button" className="btn btn-outline-primary" onClick={() => apply()} disabled={!code.trim() || checking}>
          {checking ? <span className="spinner-border spinner-border-sm" /> : "Apply"}
        </button>
      </div>
      {error && <small className="text-danger d-block mt-1">{error}</small>}

      {available.length > 0 && (
        <>
          <button type="button" className="btn btn-link btn-sm p-0 mt-1" onClick={() => setShowList((s) => !s)}>
            {showList ? "Hide" : "View"} available coupons ({available.length})
          </button>
          {showList && (
            <div className="pos-coupon-list mt-1">
              {available.map((c) => {
                const short = (c.minimumCartValue || 0) - subtotal;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className="pos-coupon-item"
                    disabled={checking || short > 0}
                    onClick={() => apply(c.code)}
                  >
                    <span className="fw-semibold">{c.code}</span>
                    <small className="d-block">{describe(c)}</small>
                    <small className={`d-block ${short > 0 ? "text-danger" : "text-muted"}`}>
                      {short > 0
                        ? `Add ${inr(short)} more`
                        : c.minimumCartValue
                          ? `Min. bill ${inr(c.minimumCartValue)}`
                          : "No minimum"}
                    </small>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}